
import React, { useState, useEffect, useRef } from 'react';
import { getOptimizedUrl } from '../utils/fileHelpers';

interface ImageWithLoaderProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  containerClassName?: string;
  scale?: number;
  disableTransition?: boolean;
  priority?: boolean;
}

export const ImageWithLoader: React.FC<ImageWithLoaderProps> = ({ 
  className, 
  containerClassName, 
  src, 
  scale = 1.0, 
  style, 
  disableTransition = false,
  priority = false,
  loading = 'lazy',
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [prevSrc, setPrevSrc] = useState(src);
  const imgRef = useRef<HTMLImageElement>(null);

  const optimizedSrc = getOptimizedUrl(src || '');

  if (src !== prevSrc) {
    setPrevSrc(src);
    setIsLoaded(false);
    setHasError(false);
  }

  useEffect(() => {
    if (!src) {
        const timer = setTimeout(() => setHasError(true), 0);
        return () => clearTimeout(timer);
    }
    if (imgRef.current && imgRef.current.complete) {
      const timer = setTimeout(() => setIsLoaded(true), 0);
      return () => clearTimeout(timer);
    }
  }, [src]);

  // 计算位移：将 X% Y% 坐标系转换为相对于中心的平移量
  let translateX = 0;
  let translateY = 0;
  if (style?.objectPosition && typeof style.objectPosition === 'string') {
    const parts = style.objectPosition.split(' ');
    const rawX = parseFloat(parts[0]);
    const rawY = parseFloat(parts[1]);
    translateX = isNaN(rawX) ? 0 : rawX - 50;
    translateY = isNaN(rawY) ? 0 : rawY - 50;
  }

  const combinedStyle: React.CSSProperties = {
    ...style, 
    objectFit: 'cover', 
    objectPosition: '50% 50%',
    transform: `translate(${translateX}%, ${translateY}%) scale(${scale})`,
    transition: disableTransition ? 'none' : 'transform 0.4s cubic-bezier(0.25, 1, 0.5, 1)',
    maxWidth: 'none',
    maxHeight: 'none',
  };

  return (
    <div className={`relative overflow-hidden bg-slate-100 flex items-center justify-center ${containerClassName || ''}`}>
      {!isLoaded && !hasError && !priority && (
        <div className="absolute inset-0 bg-slate-50 animate-pulse flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-primary-100 border-t-primary-500 rounded-full animate-spin"></div>
        </div>
      )}
      
      {hasError ? (
        <div className="w-full h-full bg-slate-100 flex flex-col items-center justify-center p-4 text-slate-300">
           <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
           <span className="text-[9px] font-black uppercase tracking-widest mt-2">Loss</span>
        </div>
      ) : (
        <img
          ref={imgRef}
          src={optimizedSrc || undefined}
          {...props}
          style={combinedStyle}
          loading={priority ? 'eager' : loading}
          fetchPriority={priority ? 'high' : 'auto'}
          className={`${className} ${isLoaded || priority ? 'opacity-100' : 'opacity-0 transition-opacity duration-300'}`}
          onLoad={() => setIsLoaded(true)}
          onError={() => {
              setHasError(true);
              setIsLoaded(true);
          }}
        />
      )}
    </div>
  );
};
